/**
 * Settings → Archive (task 865).
 *
 * Where deleted resources live. Every delete in the app writes a row to the
 * account's deletion ledger (task 862) naming the resource and everything
 * tombstoned alongside it in the same transaction; this page is a read of that
 * ledger, so "what is in the Archive" and "what a restore brings back" are one
 * list by construction rather than two lists that have to be kept in step.
 *
 * **Restore is the only action.** There is no permanent delete and no expiry:
 * by decision, nothing on this platform hard-deletes and the Archive keeps
 * every entry for as long as the account exists. A "Delete permanently" here
 * would be the one irreversible button in the product, and the report that
 * designed this page ruled it out explicitly.
 *
 * Two things can stop a restore, and both are SAID rather than worked around:
 *
 * 1. **A name or path collision.** Deleting frees the name, so a User may have
 *    built a new `summarize` since. The dialog names the live resource; the
 *    User renames one of them and tries again.
 * 2. **A dependency that is itself still deleted.** Restoring an AI Service
 *    whose collaboration config is also in the Archive would bring the service
 *    back pointing at nothing. The service answers with the missing rows AND
 *    each one's own ledger id, which is exactly what turns this from a dead end
 *    into **Restore together** — the dependencies first, in order, then the
 *    entry the User asked for.
 */
import { useMemo, useState } from 'react';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableRow,
  TableHeader,
} from '../components/ui/DataTable';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { UserName } from '../components/ui/UserName';
import { useArchive, useRestoreDeletion } from '../hooks/useArchive';
import { useFlag } from '../hooks/useFeatureFlags';
import { useOptionalToast } from '../contexts/ToastContext';
import { formatRelativeTime } from '../utils/dateUtils';
import { asApiRefusal, refusalReason } from '../utils/apiRefusal';
import {
  archiveEntityLabel,
  DEPENDENCY_ARCHIVED_CODE,
  NAME_IN_USE_CODE,
  type ArchiveEntry,
  type RestoreMissingDependency,
  type RestoreNameConflict,
} from '../types/archive';

/** What the service refused with, in the two shapes this page can act on. */
type Refusal =
  | { kind: 'name-in-use'; message: string; conflict: RestoreNameConflict | null }
  | { kind: 'dependency-archived'; message: string; missing: RestoreMissingDependency[] }
  | { kind: 'other'; message: string };

/**
 * Read the refusal off the rejected request. The response interceptor lifts
 * `data.error` onto `message`, `data.details` onto `details`, and keeps the raw
 * body on `response` — which is where the `code` lives.
 */
function readRefusal(error: unknown): Refusal {
  const err = asApiRefusal(error);
  const body = (err as { response?: { code?: string } }).response;
  const details = err.details as
    | { conflict?: RestoreNameConflict; missing?: RestoreMissingDependency[] }
    | undefined;
  const message = refusalReason(error);

  if (body?.code === NAME_IN_USE_CODE) {
    return { kind: 'name-in-use', message, conflict: details?.conflict ?? null };
  }
  if (body?.code === DEPENDENCY_ARCHIVED_CODE) {
    return { kind: 'dependency-archived', message, missing: details?.missing ?? [] };
  }
  return { kind: 'other', message };
}

/** "3 saved tests, 1 generated interface" — what went with the parent. */
function childSummary(entry: ArchiveEntry): string {
  if (entry.children.length === 0) return '—';
  const counts = new Map<string, number>();
  for (const child of entry.children) {
    counts.set(child.entityType, (counts.get(child.entityType) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([type, count]) => `${count} ${archiveEntityLabel(type).toLowerCase()}${count === 1 ? '' : 's'}`)
    .join(', ');
}

export default function ArchivePage() {
  const { data: entries = [], isLoading, isError } = useArchive();
  const restore = useRestoreDeletion();
  const toast = useOptionalToast();
  /**
   * Restore's own lever (task 1218): Restore and Restore together re-create
   * live resources. Off, the Archive still lists everything; nothing can be
   * brought back from here. The page stays on `innovate.fe.archive.page`.
   */
  const restoreEnabled = useFlag('innovate.fe.archive.restore');

  const [pending, setPending] = useState<ArchiveEntry | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const closeDialog = () => {
    setPending(null);
    setRefusal(null);
  };

  /**
   * Restore `entry`, optionally preceded by the ledger entries it depends on.
   * The dependencies go FIRST and in the order the service listed them, because
   * that order is the dependency chain — restoring the parent first is the
   * refusal we are here to clear.
   */
  const runRestore = async (entry: ArchiveEntry, first: RestoreMissingDependency[] = []) => {
    try {
      for (const dependency of first) {
        await restore.mutateAsync(dependency.deletionId);
      }
      await restore.mutateAsync(entry.id);
      closeDialog();
      toast?.success(
        `Restored ${entry.displayName}`,
        first.length > 0
          ? `Its ${first.length === 1 ? 'dependency' : 'dependencies'} came back with it.`
          : undefined
      );
    } catch (error) {
      setRefusal(readRefusal(error));
    }
  };

  const dialogBody = useMemo(() => {
    if (!pending) return null;

    if (refusal?.kind === 'name-in-use') {
      return (
        <div className="space-y-3" data-testid="archive-refusal-name-in-use">
          <p>{refusal.message}</p>
          {refusal.conflict && (
            <p>
              <span className="font-semibold">{refusal.conflict.name}</span> is live and already
              uses that name. Rename one of them, then restore again.
            </p>
          )}
        </div>
      );
    }

    if (refusal?.kind === 'dependency-archived') {
      return (
        <div className="space-y-3" data-testid="archive-refusal-dependency">
          <p>{refusal.message}</p>
          <ul className="list-disc pl-5 space-y-1" data-testid="archive-missing-list">
            {refusal.missing.map((dependency) => (
              <li key={dependency.deletionId} data-testid={`archive-missing-${dependency.id}`}>
                <span className="font-medium">{dependency.name}</span>{' '}
                <span className="text-gray-500 dark:text-gray-400">
                  ({archiveEntityLabel(dependency.entityType)})
                </span>
              </li>
            ))}
          </ul>
          <p>Restore together brings {refusal.missing.length === 1 ? 'it' : 'them'} back first.</p>
        </div>
      );
    }

    return (
      <div className="space-y-3" data-testid="archive-restore-body">
        {refusal && <p className="text-red-600 dark:text-red-400">{refusal.message}</p>}
        <p>
          {archiveEntityLabel(pending.entityType)} <span className="font-semibold">{pending.displayName}</span>{' '}
          comes back exactly as it was.
        </p>
        {pending.children.length > 0 && <p>Everything that went with it returns too: {childSummary(pending)}.</p>}
      </div>
    );
  }, [pending, refusal]);

  if (isError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400" data-testid="archive-error">
          The Archive could not be loaded. Please try again.
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Archive"
        description="Everything you have deleted on this account. Nothing here expires, and anything here can be restored."
      />

      {isLoading ? (
        <div className="mt-6 text-sm text-gray-500 dark:text-gray-400" data-testid="archive-loading">
          Loading the Archive…
        </div>
      ) : entries.length === 0 ? (
        <div
          className="mt-6 rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800"
          data-testid="archive-empty"
        >
          <ArchiveBoxIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Nothing deleted yet. Deleted AI Services, agents, prompts and other resources appear here
            and can be restored.
          </p>
        </div>
      ) : (
        <div className="mt-6">
          <DataTable>
            <DataTableHead>
              <TableHeader className="w-40">Type</TableHeader>
              <TableHeader>Name</TableHeader>
              <TableHeader>What went with it</TableHeader>
              <TableHeader className="w-40">Deleted</TableHeader>
              <TableHeader className="w-28">
                <span className="sr-only">Actions</span>
              </TableHeader>
            </DataTableHead>
            <DataTableBody>
              {entries.map((entry) => (
                // Keyed and addressed by the RESOURCE id, not the ledger id: a
                // caller arriving from a delete toast knows what it deleted, not
                // which ledger row recorded it.
                <DataTableRow key={entry.id}>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {archiveEntityLabel(entry.entityType)}
                  </DataTableCell>
                  <DataTableCell>
                    <div
                      className="text-sm font-medium text-gray-900 dark:text-white"
                      data-testid={`archive-row-${entry.entityId}`}
                    >
                      {entry.displayName}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="text-sm text-gray-500 dark:text-gray-400">
                    <span data-testid={`archive-children-${entry.entityId}`}>{childSummary(entry)}</span>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatRelativeTime(entry.deletedAt)}
                    {entry.deletedBy && (
                      <span className="block text-xs">
                        by <UserName userId={entry.deletedBy} />
                      </span>
                    )}
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-right">
                    {restoreEnabled && (
                      <button
                        type="button"
                        data-testid={`archive-restore-${entry.entityId}`}
                        onClick={() => {
                          setRefusal(null);
                          setPending(entry);
                        }}
                        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                      >
                        Restore
                      </button>
                    )}
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        </div>
      )}

      {restoreEnabled && pending && (
        <ConfirmDialog
          isOpen
          onClose={closeDialog}
          onConfirm={() =>
            void runRestore(
              pending,
              refusal?.kind === 'dependency-archived' ? refusal.missing : []
            )
          }
          isLoading={restore.isPending}
          confirmStyle="primary"
          // The one refusal with nothing to act on inside the dialog is a name
          // collision: the fix is a rename somewhere else, so there is no button
          // here that could do it.
          hideConfirm={refusal?.kind === 'name-in-use'}
          cancelLabel={refusal?.kind === 'name-in-use' ? 'Close' : 'Cancel'}
          confirmLabel={refusal?.kind === 'dependency-archived' ? 'Restore together' : 'Restore'}
          title={
            refusal?.kind === 'name-in-use'
              ? `Cannot restore “${pending.displayName}” yet`
              : `Restore “${pending.displayName}”?`
          }
          message={dialogBody}
        />
      )}
    </div>
  );
}
